import { useEffect, useState } from "react";
import {
  Page,
  Card,
  Tabs,
  IndexTable,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Modal,
  TextField,
  Link,
  DropZone,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatFriendlyDate } from "../../utility/funcs";
import { MailThreadModal, THREAD_STATUS, threadLine } from "./MailThread";

// CFDIs received at gastos@gomove.mx (gastos_inbox robot, every 10 min), or
// uploaded here by hand (XML downloaded from SAT or sent by the merchant some
// other way) — uploads go through the same gastos_inbox checks and matching.
// Matched ones are already linked to their trasladista ticket; this page is
// where a human resolves what the robot would not guess: CFDIs several tickets
// could take ("Por revisar"), and reviews what it rejected. gastos@ receives
// every invoice GoMove gets, so a CFDI no ticket could take waits quietly under
// "Sin ticket" (no badge) and moves to "Por revisar" — or is matched outright —
// once a ticket that fits it arrives.

const TABS = [
  { id: "to_invoice", content: "Por facturar" },
  { id: "unmatched", content: "Por revisar", status: "unmatched", candidate: true },
  { id: "no_ticket", content: "Sin ticket", status: "unmatched", candidate: false },
  { id: "matched", content: "Conciliadas", status: "matched" },
  { id: "rejected", content: "Rechazadas", status: "rejected" },
];
const KIND_LABEL = {
  gasolina: "Gasolina",
  peaje: "Caseta",
  regreso: "Regreso del chofer",
  herramienta: "Herramienta",
  comida: "Comida",
  verificacion: "Verificación",
  otro: "Otro",
};

function CopyChip({ value }) {
  const [ok, setOk] = useState(false);
  return (
    <Button
      size="micro"
      variant="plain"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setOk(true);
          setTimeout(() => setOk(false), 1200);
        } catch {
          /* ignore */
        }
      }}
    >
      {ok ? "¡Copiado!" : value}
    </Button>
  );
}

// Stations whose portal asks for an image CAPTCHA (facturador-svc status
// "captcha"): a person types it here and the robot does the rest — adds the
// ticket, registers/picks the RFC, stamps and brings the XML back.
function CaptchaBox({ ticket, onDone }) {
  const [img, setImg] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const call = async (body) => {
    const { data, error } = await supabase.functions.invoke("gastos_inbox/captcha", { body: { ticket_id: ticket.id, ...body } });
    // invoke() hides the JSON of a non-2xx reply inside error.context.
    if (error) {
      const detail = await error.context?.json?.().catch(() => null);
      return detail || { error: error.message };
    }
    return data;
  };
  const fetchImage = async (note = null) => {
    setBusy(true);
    setAnswer("");
    setMsg(note);
    const r = await call({});
    setBusy(false);
    if (r?.challenge) setImg(r.challenge);
    else {
      setImg(null);
      setMsg(r?.error || "No se pudo traer la imagen");
      if (r?.job) onDone();
    }
  };
  const send = async () => {
    setBusy(true);
    setMsg(null);
    const r = await call({ answer });
    setBusy(false);
    if (r?.retry) return fetchImage(r.job?.last_error || r.error || "No coincidió; escribe la nueva imagen");
    if (r?.error) return setMsg(r.error);
    setImg(null);
    onDone();
  };

  if (!img)
    return (
      <BlockStack gap="100">
        <Button size="slim" variant="primary" loading={busy} onClick={() => fetchImage()}>
          Facturar (escribir CAPTCHA)
        </Button>
        {msg && <Text tone="critical" variant="bodySm">{msg}</Text>}
      </BlockStack>
    );
  return (
    <BlockStack gap="100">
      {img.station && <Text variant="bodySm" tone="subdued">{img.station}</Text>}
      <img src={`data:${img.mime};base64,${img.image_base64}`} alt="CAPTCHA" style={{ width: 180, height: 50, border: "1px solid #ddd" }} />
      <InlineStack gap="100" wrap={false} blockAlign="end">
        <div style={{ width: 110 }} onKeyDown={(e) => e.key === "Enter" && answer.trim() && !busy && send()}>
          <TextField label="Código" labelHidden autoComplete="off" value={answer} maxLength={5} onChange={(v) => setAnswer(v.toUpperCase())} autoFocus />
        </div>
        <Button size="slim" variant="primary" loading={busy} disabled={!answer.trim()} onClick={send}>
          Enviar
        </Button>
        <Button size="slim" variant="plain" disabled={busy} onClick={() => fetchImage()}>
          Otra imagen
        </Button>
      </InlineStack>
      {msg && <Text tone="critical" variant="bodySm">{msg}</Text>}
      {img.notice && <Text tone="caution" variant="bodySm">{img.notice}</Text>}
    </BlockStack>
  );
}

// Portal tickets GoMove has to invoice itself: the trasladista uploaded the
// ticket and the merchant invoices online. Oldest deadline first.
// Also shown on /traslados_pendientes; onCount lets that page hide it when empty.
export function PorFacturar({ canEdit, onCount }) {
  const [rows, setRows] = useState([]);
  const [fiscal, setFiscal] = useState(null);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Email invoicing threads (facturador-svc mail agent), by ticket id.
  const [threads, setThreads] = useState({});
  const [openThread, setOpenThread] = useState(null);

  const load = async () => {
    setLoading(true);
    const [{ data }, { data: f }] = await Promise.all([
      supabase
        .from("traslado_tickets")
        .select("*, trasladista:trasladistas(name)")
        // Every ticket still waiting for its CFDI (or paid ahead and still needing
        // it), from the moment it's uploaded — portal AND "en caja": a counter
        // ticket whose CFDI never arrives must not be invisible (Transpaís, 2026-10-07).
        .or("status.eq.pending_cfdi,and(status.eq.approved,cfdi_pending.eq.true)")
        .order("invoice_available_from", { ascending: true, nullsFirst: true })
        .order("ticket_date", { ascending: true }),
      supabase.rpc("gomove_fiscal_profile"),
    ]);
    setRows(data || []);
    onCount?.((data || []).length);
    setFiscal(f);
    if (data?.length) {
      const { data: th } = await supabase.from("invoice_threads").select("*").in("ticket_id", data.map((t) => t.id));
      setThreads(Object.fromEntries((th || []).map((x) => [x.ticket_id, x])));
      const { data: signed } = await supabase.storage
        .from("traslado-tickets")
        .createSignedUrls(data.map((t) => t.photo_path), 3600);
      setUrls(Object.fromEntries((signed || []).map((x) => [x.path, x.signedUrl])));
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // Thread badges update live as the AI writes / the merchant answers.
    const ch = supabase
      .channel("invoice-threads-por-facturar")
      .on("postgres_changes", { event: "*", schema: "public", table: "invoice_threads" }, (p) => {
        if (p.new?.ticket_id) setThreads((prev) => ({ ...prev, [p.new.ticket_id]: p.new }));
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const markRequested = async (t, requested) => {
    const { error: err } = await supabase.rpc("mark_ticket_invoice_requested", {
      p_ticket: t.id,
      p_requested: requested,
    });
    if (err) setError(err.message);
    else load();
  };

  // Deadline = end of the month in which the ticket can be invoiced.
  const deadline = (t) => {
    const base = t.invoice_available_from || t.ticket_date;
    const x = base ? new Date(`${base}T12:00:00`) : new Date();
    return new Date(x.getFullYear(), x.getMonth() + 1, 0).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
    });
  };
  const fmtDay = (d) =>
    new Date(`${d}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  const waiting = (t) =>
    t.invoice_available_from && new Date(`${t.invoice_available_from}T00:00:00`) > new Date();

  // facturador-svc (the robot on the VPS) state, mirrored by gastos_inbox/facturador.
  const robot = (t) => {
    const s = t.facturador_status;
    if (!s) return null;
    const next = t.facturador_next_at
      ? new Date(t.facturador_next_at).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
      : null;
    return (
      {
        submitted: { tone: "info", label: "Robot: leyendo ticket" },
        identifying: { tone: "info", label: "Robot: leyendo ticket" },
        waiting: { tone: "info", label: next ? `Robot: reintenta ${next}` : "Robot: en cola" },
        running: { tone: "info", label: "Robot: facturando" },
        ready: { tone: "attention", label: "Robot: listo, falta aprobar" },
        done: { tone: "success", label: "Robot: facturada, llegando" },
        issued: { tone: "success", label: "Robot: facturada, XML por llegar" },
        manual: { tone: "attention", label: "Facturar a mano (CAPTCHA) — datos listos" },
        captcha: { tone: "attention", label: "Robot: falta escribir el CAPTCHA" },
        needs_attention: { tone: "critical", label: "Robot: necesita revisión" },
        unsupported: { tone: undefined, label: "Robot: portal no soportado" },
        failed: { tone: "critical", label: "Robot: venció el plazo" },
        cancelled: { tone: undefined, label: "Robot: cancelado" },
      }[s] || { tone: undefined, label: `Robot: ${s}` }
    );
  };

  // "En caja" → portal: the traslado_capture sweep (every 15 min) then sends it to the robot.
  const toPortal = async (t) => {
    const { error: err } = await supabase.rpc("set_ticket_invoice_mode", { p_ticket: t.id, p_mode: "portal" });
    if (err) setError(err.message);
    else load();
  };

  const setAvailable = async (t, date) => {
    const { error: err } = await supabase.rpc("set_ticket_available_from", {
      p_ticket: t.id,
      p_date: date || null,
    });
    if (err) setError(err.message);
    else load();
  };

  return (
    <BlockStack gap="300">
      {fiscal && (
        <div style={{ padding: "12px 16px 0" }}>
          <InlineStack gap="300" blockAlign="center">
            <Text tone="subdued" variant="bodySm">Datos GoMove:</Text>
            <CopyChip value={fiscal.rfc} />
            <CopyChip value={fiscal.razon_social} />
            <CopyChip value={fiscal.cp} />
            <Text variant="bodySm">{`${fiscal.regimen} · ${fiscal.uso_cfdi}`}</Text>
            <CopyChip value={fiscal.email} />
          </InlineStack>
        </div>
      )}
      {error && <Banner tone="critical">{error}</Banner>}
      <IndexTable
        resourceName={{ singular: "ticket", plural: "tickets" }}
        itemCount={rows.length}
        loading={loading}
        selectable={false}
        emptyState={<div style={{ padding: 24 }}><Text tone="subdued">No hay tickets por facturar.</Text></div>}
        headings={[
          { title: "Ticket" },
          { title: "Comercio" },
          { title: "Dato para facturar" },
          { title: "Total", alignment: "end" },
          { title: "Fecha límite" },
          { title: "" },
        ]}
      >
        {rows.map((t, i) => (
          <IndexTable.Row id={t.id} key={t.id} position={i}>
            <IndexTable.Cell>
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <a href={urls[t.photo_path]} target="_blank" rel="noreferrer">
                  <img
                    src={urls[t.photo_path]}
                    alt="ticket"
                    style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6, background: "#eee" }}
                  />
                </a>
                <BlockStack gap="0">
                  <Link url={`/vehicle_transfers/${t.traslado_order}`}>{`Traslado #${t.traslado_order}`}</Link>
                  <Text tone="subdued" variant="bodySm">{t.trasladista?.name || "—"}</Text>
                </BlockStack>
              </InlineStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <BlockStack gap="0">
                <Text>{t.merchant_name || "—"}</Text>
                <Text tone="subdued" variant="bodySm">{t.merchant_rfc || ""}</Text>
              </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              {t.invoice_ref ? <CopyChip value={t.invoice_ref} /> : "—"}
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text alignment="end" fontWeight="semibold">{formatPrice(t.total)}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <BlockStack gap="050">
                <Text>{deadline(t)}</Text>
                {waiting(t) && (
                  <Badge tone="attention">{`Disponible a partir del ${fmtDay(t.invoice_available_from)}`}</Badge>
                )}
                {canEdit && (
                  <div style={{ maxWidth: 150 }}>
                    <TextField
                      label="Facturable desde"
                      type="date"
                      autoComplete="off"
                      value={t.invoice_available_from || ""}
                      onChange={(v) => setAvailable(t, v)}
                    />
                  </div>
                )}
              </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <BlockStack gap="100">
              {t.invoice_mode !== "portal" && (
                <InlineStack gap="200" blockAlign="center" wrap={false}>
                  <Badge>En caja · trasladista</Badge>
                  {canEdit && (
                    <Button size="slim" variant="plain" onClick={() => toPortal(t)}>
                      Pasar a portal
                    </Button>
                  )}
                </InlineStack>
              )}
              {t.facturador_status === "captcha" && canEdit && <CaptchaBox ticket={t} onDone={load} />}
              {t.facturador_status === "manual" && t.facturador_manual && (
                <BlockStack gap="100">
                  {t.facturador_manual.portal && (
                    <Button size="slim" url={t.facturador_manual.portal} external>
                      {t.facturador_manual.station ? `Abrir portal · ${t.facturador_manual.station}` : "Abrir portal"}
                    </Button>
                  )}
                  {(t.facturador_manual.steps || []).map((st) => (
                    <InlineStack key={st.label} gap="100" blockAlign="center" wrap={false}>
                      <Text tone="subdued" variant="bodySm">{st.label}:</Text>
                      {/^\$?[\w.-]{2,40}$/.test(String(st.value)) ? <CopyChip value={String(st.value)} /> : <Text variant="bodySm">{st.value}</Text>}
                    </InlineStack>
                  ))}
                  {t.facturador_manual.deadline && (
                    <Text tone="caution" variant="bodySm">{`Antes del ${t.facturador_manual.deadline}`}</Text>
                  )}
                  {t.facturador_manual.note && <Text tone="subdued" variant="bodySm">{t.facturador_manual.note}</Text>}
                </BlockStack>
              )}
              {threads[t.id] && (
                <BlockStack gap="050">
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <Badge tone={THREAD_STATUS[threads[t.id].status]?.tone}>
                      {THREAD_STATUS[threads[t.id].status]?.label || threads[t.id].status}
                    </Badge>
                    <Button size="slim" variant="plain" onClick={() => setOpenThread(t.id)}>
                      Conversación
                    </Button>
                  </InlineStack>
                  {threadLine(threads[t.id]) && <Text tone="subdued" variant="bodySm">{threadLine(threads[t.id])}</Text>}
                </BlockStack>
              )}
              {robot(t) && !threads[t.id] && (
                <BlockStack gap="0">
                  <Badge tone={robot(t).tone}>{robot(t).label}</Badge>
                  {t.facturador_note && ["needs_attention", "failed", "waiting", "captcha"].includes(t.facturador_status) && (
                    <Text tone="subdued" variant="bodySm">{t.facturador_note}</Text>
                  )}
                </BlockStack>
              )}
              <InlineStack gap="200" wrap={false} blockAlign="center">
                {t.ai?.invoice_url && (
                  <Button
                    size="slim"
                    url={/^https?:/i.test(t.ai.invoice_url) ? t.ai.invoice_url : `https://${t.ai.invoice_url}`}
                    external
                  >
                    Abrir portal
                  </Button>
                )}
                {t.invoice_requested_at ? (
                  <Button size="slim" variant="plain" onClick={() => canEdit && markRequested(t, false)}>
                    {`Solicitada ${new Date(t.invoice_requested_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}`}
                  </Button>
                ) : (
                  canEdit && (
                    <Button size="slim" variant="plain" onClick={() => markRequested(t, true)}>
                      Marcar solicitada
                    </Button>
                  )
                )}
              </InlineStack>
              </BlockStack>
            </IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
      {openThread && (
        <MailThreadModal ticketId={openThread} open onClose={() => setOpenThread(null)} canEdit={canEdit} />
      )}
    </BlockStack>
  );
}

function GastosFacturas() {
  const { hasPermission } = UserAuth();
  const canEdit = hasPermission("services.create");
  // GoMove's latest Constancia de Situación Fiscal: facturador-svc downloads it
  // from SAT every month (fiscal_documents / bucket fiscal-docs).
  const [csfLoading, setCsfLoading] = useState(false);
  const [csfError, setCsfError] = useState(null);
  const downloadCsf = async () => {
    setCsfLoading(true);
    setCsfError(null);
    const { data: doc } = await supabase
      .from("fiscal_documents")
      .select("period, storage_path")
      .eq("kind", "csf")
      .order("period", { ascending: false })
      .limit(1)
      .maybeSingle();
    const { data: signed, error } = doc
      ? await supabase.storage.from("fiscal-docs").createSignedUrl(doc.storage_path, 300, { download: `CSF_GOMOVE_${doc.period}.pdf` })
      : { data: null, error: { message: "Todavía no hay una CSF descargada del SAT." } };
    setCsfLoading(false);
    if (error) setCsfError(error.message);
    // Served as an attachment, so this downloads without leaving the page (and
    // isn't caught by popup blockers the way a window.open after an await is).
    else window.location.assign(signed.signedUrl);
  };
  // ?tab=<id> opens a given tab (e.g. from /traslados_pendientes).
  const [tab, setTab] = useState(() => {
    const i = TABS.findIndex((t) => t.id === new URLSearchParams(window.location.search).get("tab"));
    return i >= 0 ? i : 0;
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mailbox, setMailbox] = useState(undefined); // undefined = not loaded yet
  const [error, setError] = useState(null);

  const [assigning, setAssigning] = useState(null); // cfdi row
  const [candidates, setCandidates] = useState([]);
  const [showAll, setShowAll] = useState(false); // also list candidates with a different total
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("expense_cfdis")
      .select(
        "*, ticket:traslado_tickets(id, kind, total, ticket_date, traslado_order, trasladista:trasladistas(name)), expense:traslado_expenses(id, kind, amount, notes, traslado_order)",
      )
      .eq("status", TABS[tab].status);
    if (TABS[tab].candidate !== undefined) q = q.eq("has_ticket_candidate", TABS[tab].candidate);
    const [{ data }, { data: state }] = await Promise.all([
      q.order("created_at", { ascending: false }).limit(200),
      supabase.from("mailbox_sync_state").select("*").limit(1).maybeSingle(),
    ]);
    setRows(data || []);
    setMailbox(state);
    setLoading(false);
  };

  // Mailbox status is shown on every tab, including "Por facturar" (which
  // doesn't run load()), so fetch it on its own.
  useEffect(() => {
    supabase
      .from("mailbox_sync_state")
      .select("*")
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setMailbox(data));
  }, []);

  useEffect(() => {
    if (TABS[tab].id === "to_invoice") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ------------------------------------------------------ manual upload
  const UPLOAD_EXT = /\.(xml|pdf|zip)$/i;
  const addUploadFiles = (_all, accepted) => {
    const ok = accepted.filter((f) => UPLOAD_EXT.test(f.name));
    setUploadFiles((prev) => [...prev, ...ok.filter((f) => !prev.some((p) => p.name === f.name))]);
    setUploadResults(null);
    setUploadError(ok.length < accepted.length ? "Solo se aceptan archivos .xml, .pdf o .zip" : null);
  };
  const closeUpload = () => {
    setUploadOpen(false);
    setUploadFiles([]);
    setUploadResults(null);
    setUploadError(null);
  };
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",")[1] || "");
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  const upload = async () => {
    setUploading(true);
    setUploadError(null);
    try {
      const files = await Promise.all(uploadFiles.map(async (f) => ({ name: f.name, base64: await toBase64(f) })));
      const { data, error: err } = await supabase.functions.invoke("gastos_inbox/upload", { body: { files } });
      if (err) {
        let msg = err.message;
        try {
          msg = (await err.context.json()).error || msg;
        } catch {
          /* keep generic message */
        }
        throw new Error(msg);
      }
      setUploadResults(data.results || []);
      setUploadFiles([]);
      if (TABS[tab].id !== "to_invoice") load();
    } catch (e) {
      setUploadError(e.message || "No se pudo subir");
    } finally {
      setUploading(false);
    }
  };
  const resultLine = (r) => {
    if (r.skipped === "no_es_cfdi") return { tone: "warning", badge: "No es CFDI", text: `${r.file}: no tiene timbre fiscal` };
    if (r.skipped === "already_received")
      return { tone: "info", badge: "Ya estaba", text: `Ya se había recibido (${r.status || "registrada"})` };
    if (r.status === "matched") return { tone: "success", badge: "Conciliada", text: "Se asignó a su ticket automáticamente" };
    if (r.status === "rejected") return { tone: "critical", badge: "Rechazada", text: r.reject_reason };
    return { tone: "attention", badge: "Sin conciliar", text: "No se encontró un ticket único; queda en «Por revisar» o «Sin ticket»" };
  };

  const openFile = async (path) => {
    const { data } = await supabase.storage.from("expense-cfdis").createSignedUrl(path, 600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  // Candidates: trasladista tickets waiting for their CFDI, and expenses GoMove
  // paid directly (e.g. a flight on the company card) that have none yet.
  const openAssign = async (cfdi) => {
    setAssigning(cfdi);
    setShowAll(false);
    const [{ data: tickets }, { data: expenses }] = await Promise.all([
      supabase
        .from("traslado_tickets")
        .select("id, kind, total, ticket_date, merchant_name, merchant_rfc, photo_path, traslado_order, trasladista:trasladistas(name)")
        // Same set as ticket_accepts_cfdi(): includes tickets paid before their invoice arrived.
        .is("cfdi_uuid", null)
        .or("status.in.(pending_cfdi,expired),and(status.eq.approved,cfdi_pending.eq.true)")
        .order("created_at", { ascending: false })
        .limit(200),
      supabase
        .from("traslado_expenses")
        .select("id, kind, amount, notes, traslado_order, created_at, cfdi:expense_cfdis(id), pase:pase_movements(id)")
        .eq("source", "manual")
        .order("created_at", { ascending: false })
        .limit(200),
    ]);
    const sorted = [
      ...(tickets || []).map((t) => ({ ...t, type: "ticket" })),
      ...(expenses || [])
        // TAG tolls are covered by PASE's monthly invoice per tag.
        .filter((e) => !e.cfdi?.length && !e.pase?.length)
        .map((e) => ({ ...e, type: "expense", total: e.amount })),
    ]
      .map((t) => ({ ...t, diff: Math.abs(Number(t.total || 0) - Number(cfdi.total)) }))
      .sort((a, b) => a.diff - b.diff)
      .slice(0, 25);
    const photos = sorted.filter((t) => t.photo_path).map((t) => t.photo_path);
    if (photos.length) {
      const { data: signed } = await supabase.storage.from("traslado-tickets").createSignedUrls(photos, 600);
      const urls = Object.fromEntries((signed || []).map((s) => [s.path, s.signedUrl]));
      sorted.forEach((t) => (t.url = urls[t.photo_path]));
    }
    setCandidates(sorted);
  };

  const assign = async (target) => {
    setBusy(true);
    const { error: err } =
      target.type === "expense"
        ? await supabase.rpc("assign_expense_cfdi_to_expense", { p_cfdi: assigning.id, p_expense: target.id })
        : await supabase.rpc("assign_expense_cfdi", { p_cfdi: assigning.id, p_ticket: target.id });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setAssigning(null);
    load();
  };

  const reject = async () => {
    setBusy(true);
    const { error: err } = await supabase.rpc("reject_expense_cfdi", {
      p_cfdi: rejecting.id,
      p_reason: rejectReason,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setRejecting(null);
    setRejectReason("");
    load();
  };

  const satBadge = (r) =>
    r.sat_estado === "Vigente" ? (
      <Badge tone="success">SAT vigente</Badge>
    ) : r.sat_estado === "No Encontrado" ? (
      // Normal for up to 72h after stamping; the inbox asks SAT again.
      <Badge tone="attention">SAT por confirmar</Badge>
    ) : r.sat_estado ? (
      <Badge tone="critical">{`SAT: ${r.sat_estado}`}</Badge>
    ) : (
      <Badge>SAT sin consultar</Badge>
    );

  return (
    <AppWrapper>
      <Page
        title="Facturas de gastos"
        subtitle="CFDIs recibidos en gastos@gomove.mx y su ticket"
        backAction={{ url: "/vehicle_transfers" }}
        primaryAction={
          canEdit ? { content: "Subir XML", onAction: () => setUploadOpen(true) } : undefined
        }
        secondaryActions={canEdit ? [{ content: "Descargar CSF", loading: csfLoading, onAction: downloadCsf }] : []}
        fullWidth
      >
        <BlockStack gap="300">
          {csfError && (
            <Banner tone="critical" onDismiss={() => setCsfError(null)}>
              {csfError}
            </Banner>
          )}
          {mailbox?.last_error && (
            <Banner tone="critical" title="No se pudo leer el buzón">
              {mailbox.last_error}
            </Banner>
          )}
          {mailbox === null && (
            <Banner tone="warning">
              El buzón aún no se ha leído. Revisa que la contraseña de gastos@gomove.mx
              esté configurada.
            </Banner>
          )}
          {mailbox?.last_run_at && !mailbox.last_error && (
            <Text tone="subdued" variant="bodySm">
              Buzón revisado {formatFriendlyDate(mailbox.last_run_at)} (cada 30 minutos y al llegar cada correo).
            </Text>
          )}
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          <Card padding="0">
            <Tabs tabs={TABS} selected={tab} onSelect={setTab} />
            {TABS[tab].id === "to_invoice" ? (
              <PorFacturar canEdit={canEdit} />
            ) : (
            <IndexTable
              resourceName={{ singular: "factura", plural: "facturas" }}
              itemCount={rows.length}
              loading={loading}
              selectable={false}
              headings={[
                { title: "Emisor" },
                { title: "Fecha" },
                { title: "Total", alignment: "end" },
                { title: "SAT" },
                { title: TABS[tab].id === "matched" ? "Ticket" : TABS[tab].id === "rejected" ? "Motivo" : "Nota" },
                { title: "Archivos" },
                { title: "" },
              ]}
            >
              {rows.map((r, i) => (
                <IndexTable.Row id={r.id} key={r.id} position={i}>
                  <IndexTable.Cell>
                    <BlockStack gap="0">
                      <Text fontWeight="semibold">{r.emisor_nombre || r.emisor_rfc}</Text>
                      <Text tone="subdued" variant="bodySm">
                        {r.emisor_rfc} · {r.source === "upload" ? `subida a mano por ${r.from_email}` : r.from_email}
                      </Text>
                    </BlockStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{r.fecha ? formatFriendlyDate(r.fecha) : "—"}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text alignment="end" fontWeight="semibold">
                      {formatPrice(r.total)}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{satBadge(r)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    {TABS[tab].id === "matched" && r.expense ? (
                      <BlockStack gap="0">
                        <Link url={`/vehicle_transfers/${r.expense.traslado_order}`}>
                          {`Traslado #${r.expense.traslado_order}`}
                        </Link>
                        <Text tone="subdued" variant="bodySm">
                          {[KIND_LABEL[r.expense.kind], "pagado por GoMove", r.expense.notes].filter(Boolean).join(" · ")}
                        </Text>
                      </BlockStack>
                    ) : TABS[tab].id === "matched" && r.ticket ? (
                      <BlockStack gap="0">
                        <Link url={`/vehicle_transfers/${r.ticket.traslado_order}`}>
                          {`Traslado #${r.ticket.traslado_order}`}
                        </Link>
                        <Text tone="subdued" variant="bodySm">
                          {[KIND_LABEL[r.ticket.kind], r.ticket.trasladista?.name, r.matched_by ? "a mano" : "automático"]
                            .filter(Boolean)
                            .join(" · ")}
                        </Text>
                      </BlockStack>
                    ) : (
                      <Text tone="subdued">{r.reject_reason || r.notes || "—"}</Text>
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="100">
                      {r.xml_path && (
                        <Button size="micro" variant="plain" onClick={() => openFile(r.xml_path)}>
                          XML
                        </Button>
                      )}
                      {r.pdf_path && (
                        <Button size="micro" variant="plain" onClick={() => openFile(r.pdf_path)}>
                          PDF
                        </Button>
                      )}
                    </InlineStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {TABS[tab].status === "unmatched" && canEdit && (
                      <InlineStack gap="200" wrap={false}>
                        <Button size="slim" onClick={() => openAssign(r)}>
                          Asignar
                        </Button>
                        <Button size="slim" tone="critical" variant="plain" onClick={() => setRejecting(r)}>
                          Rechazar
                        </Button>
                      </InlineStack>
                    )}
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
            )}
          </Card>
        </BlockStack>

        <Modal
          open={!!assigning}
          onClose={() => setAssigning(null)}
          title={assigning ? `Asignar ${formatPrice(assigning.total)} de ${assigning.emisor_nombre || assigning.emisor_rfc}` : ""}
          large
        >
          <Modal.Section>
            {candidates.length === 0 ? (
              <Text tone="subdued">No hay tickets ni gastos pendientes de factura.</Text>
            ) : (
              <BlockStack gap="200">
                {!showAll && !candidates.some((t) => t.diff <= 0.05) && (
                  <InlineStack gap="200" blockAlign="center">
                    <Text tone="subdued">Ningún ticket o gasto sin factura tiene el mismo total.</Text>
                    <Button variant="plain" onClick={() => setShowAll(true)}>
                      Mostrar todos
                    </Button>
                  </InlineStack>
                )}
                {candidates.filter((t) => showAll || t.diff <= 0.05).map((t) => (
                  <InlineStack key={`${t.type}-${t.id}`} gap="300" blockAlign="center" wrap={false}>
                    {t.type === "ticket" ? (
                      <a href={t.url} target="_blank" rel="noreferrer">
                        <img
                          src={t.url}
                          alt="ticket"
                          style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, background: "#eee" }}
                        />
                      </a>
                    ) : (
                      <div style={{ width: 56, flexShrink: 0 }}>
                        <Badge>GoMove</Badge>
                      </div>
                    )}
                    <BlockStack gap="0">
                      <Text fontWeight="semibold">
                        {formatPrice(t.total)} · {KIND_LABEL[t.kind]}
                        {t.diff > 0.05 && (
                          <Text as="span" tone="critical">{` (diferencia ${formatPrice(t.diff)})`}</Text>
                        )}
                      </Text>
                      <Text tone="subdued" variant="bodySm">
                        {(t.type === "expense"
                          ? [`Traslado #${t.traslado_order}`, "pagado por GoMove", t.notes]
                          : [`Traslado #${t.traslado_order}`, t.trasladista?.name, t.merchant_name, t.merchant_rfc, t.ticket_date])
                          .filter(Boolean)
                          .join(" · ")}
                      </Text>
                    </BlockStack>
                    <div style={{ marginLeft: "auto" }}>
                      <Button size="slim" loading={busy} onClick={() => assign(t)}>
                        Asignar
                      </Button>
                    </div>
                  </InlineStack>
                ))}
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>

        <Modal
          open={uploadOpen}
          onClose={closeUpload}
          title="Subir factura de gasto"
          primaryAction={{
            content: uploadResults ? "Listo" : "Subir",
            loading: uploading,
            disabled: !uploadResults && !uploadFiles.length,
            onAction: uploadResults ? closeUpload : upload,
          }}
          secondaryActions={uploadResults ? [] : [{ content: "Cancelar", onAction: closeUpload }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text tone="subdued">
                Sube el XML (y su PDF, o un .zip) descargado del SAT o del portal del comercio. Pasa por
                las mismas validaciones que el correo: receptor GoMove, SAT vigente, lista EFOS y
                duplicados, y se asigna a su ticket si hay uno que coincida.
              </Text>
              {uploadError && (
                <Banner tone="critical" onDismiss={() => setUploadError(null)}>
                  {uploadError}
                </Banner>
              )}
              {!uploadResults && (
                <DropZone onDrop={addUploadFiles} allowMultiple>
                  {uploadFiles.length ? (
                    <div style={{ padding: 12 }}>
                      <BlockStack gap="100">
                        {uploadFiles.map((f) => (
                          <InlineStack key={f.name} align="space-between" blockAlign="center">
                            <Text>{f.name}</Text>
                            <Button
                              variant="plain"
                              tone="critical"
                              onClick={() => setUploadFiles((prev) => prev.filter((p) => p.name !== f.name))}
                            >
                              Quitar
                            </Button>
                          </InlineStack>
                        ))}
                      </BlockStack>
                    </div>
                  ) : (
                    <DropZone.FileUpload actionHint="Archivos .xml, .pdf o .zip" />
                  )}
                </DropZone>
              )}
              {uploadResults && !uploadResults.length && (
                <Banner tone="warning">No se encontró ningún CFDI en los archivos.</Banner>
              )}
              {uploadResults?.map((r, i) => {
                const l = resultLine(r);
                return (
                  <InlineStack key={r.uuid || i} gap="200" blockAlign="center" wrap={false}>
                    <Badge tone={l.tone}>{l.badge}</Badge>
                    <BlockStack gap="0">
                      <Text fontWeight="semibold">
                        {[r.emisor, r.total != null ? formatPrice(r.total) : null].filter(Boolean).join(" · ") || r.uuid}
                      </Text>
                      <Text tone="subdued" variant="bodySm">
                        {l.text}
                        {r.uuid ? ` · ${r.uuid}` : ""}
                        {r.sat && r.sat !== "Vigente" ? ` · SAT: ${r.sat}` : ""}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                );
              })}
            </BlockStack>
          </Modal.Section>
        </Modal>

        <Modal
          open={!!rejecting}
          onClose={() => setRejecting(null)}
          title="Rechazar factura"
          primaryAction={{
            content: "Rechazar",
            destructive: true,
            loading: busy,
            disabled: rejectReason.trim().length < 5,
            onAction: reject,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setRejecting(null) }]}
        >
          <Modal.Section>
            <TextField
              label="Motivo"
              autoComplete="off"
              value={rejectReason}
              onChange={setRejectReason}
              placeholder="Ej. No es un gasto de traslado, factura duplicada…"
            />
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default GastosFacturas;
